"use strict";

import { ActionType } from '../../ActionType';
import { State } from '../../State';
import { SingleGestureName } from '../../v3/types';
import { DEFAULT_TOUCH_SLOP, NATIVE_GESTURE_ROLE_ATTRIBUTE } from '../constants';
import { NativeGestureRole } from '../interfaces';
import { ButtonEventName, dispatchButtonEvent } from '../tools/ButtonEvents';
import { dispatchGestureLifecycleEvent, GestureLifecycleEvent } from '../tools/GestureLifecycleEvents';
import { isStylableElement } from '../utils';
import GestureHandler from './GestureHandler';
export default class NativeViewGestureHandler extends GestureHandler {
  isContinuous = true;
  role = null;

  // Documented as Android-only; on web it's used internally for the Button
  // role, while other roles intentionally activate on real interaction.
  shouldActivateOnStart = false;
  disallowInterruption = false;
  yieldsToContinuousGestures = false;
  startX = 0;
  startY = 0;
  minDistSq = DEFAULT_TOUCH_SLOP * DEFAULT_TOUCH_SLOP;
  scrollActivationThresholdSq = 2 * 2;
  isScrollDriven = false;
  scrollDetected = false;
  lastActiveHandlerData = null;
  hasLongPressHandler = false;
  longPressDuration = -1;
  longPressDetected = false;
  lastEventWasInside = false;
  pendingLongPress = null;
  constructor(delegate) {
    super(delegate);
    this.name = SingleGestureName.Native;
  }
  init(ref, propsRef, actionType, hostDetector = null) {
    super.init(ref, propsRef, actionType, hostDetector);
    this.isScrollDriven = false;
    const view = this.delegate.view;
    if (!isStylableElement(view)) {
      return;
    }
    this.restoreViewStyles(view);
    if (this.usesNativeOrVirtualDetector()) {
      this.role = view.getAttribute(NATIVE_GESTURE_ROLE_ATTRIBUTE) ?? null;
    } else {
      if (view.getAttribute('role') === 'button') {
        this.role = NativeGestureRole.Button;
      } else if (view.querySelector(':scope > input[role="switch"]') !== null) {
        this.role = NativeGestureRole.Switch;
      }
    }
    this.isScrollDriven = this.role === NativeGestureRole.ScrollView;
  }
  updateGestureConfig(config) {
    super.updateGestureConfig(config);
    if (config.shouldActivateOnStart !== undefined) {
      this.shouldActivateOnStart = config.shouldActivateOnStart;
    }
    if (config.disallowInterruption !== undefined) {
      this.disallowInterruption = config.disallowInterruption;
    }
    if (config.yieldsToContinuousGestures !== undefined) {
      this.yieldsToContinuousGestures = config.yieldsToContinuousGestures;
    }
    if (config.hasLongPressHandler !== undefined) {
      this.hasLongPressHandler = config.hasLongPressHandler;
    }
    if (config.longPressDuration !== undefined) {
      this.longPressDuration = config.longPressDuration;
    }
    const view = this.delegate.view;
    if (isStylableElement(view)) {
      this.restoreViewStyles(view);
    }
  }
  resetConfig() {
    super.resetConfig();
    this.shouldCancelWhenOutside = true;
    this.shouldActivateOnStart = false;
    this.disallowInterruption = false;
    this.yieldsToContinuousGestures = false;
    this.hasLongPressHandler = false;
    this.longPressDuration = -1;
  }
  restoreViewStyles(view) {
    if (!view) {
      return;
    }
    view.style['touchAction'] = 'auto';
    // @ts-ignore Turns on default touch behavior on Safari
    view.style['WebkitTouchCallout'] = 'auto';
  }
  onPointerDown(event) {
    this.tracker.addToTracker(event);
    super.onPointerDown(event);
    this.newPointerAction();
  }
  onPointerAdd(event) {
    this.tracker.addToTracker(event);
    super.onPointerAdd(event);
    this.newPointerAction();
  }
  newPointerAction() {
    const lastCoords = this.tracker.getAbsoluteCoordsAverage();
    this.startX = lastCoords.x;
    this.startY = lastCoords.y;
    if (this.state !== State.UNDETERMINED) {
      return;
    }
    this.scrollDetected = false;
    this.begin();
    dispatchGestureLifecycleEvent(this.delegate.view, GestureLifecycleEvent.Began);
    const view = this.delegate.view;
    const isRNGHText = view.hasAttribute('rnghtext');
    if (this.role === NativeGestureRole.Button && this.shouldActivateOnStart || this.role === NativeGestureRole.Switch || isRNGHText) {
      this.activate();
    }
  }
  onPointerMove(event) {
    this.tracker.track(event);
    if (this.role === NativeGestureRole.Switch || this.role === NativeGestureRole.Button) {
      return;
    }
    if (this.isScrollDriven) {
      this.tryScrollDrivenActivation();
      return;
    }
    if (this.pointerTravelSq() >= this.minDistSq && this.state === State.BEGAN) {
      this.activate();
    }
  }
  pointerTravelSq() {
    const lastCoords = this.tracker.getAbsoluteCoordsAverage();
    const dx = this.startX - lastCoords.x;
    const dy = this.startY - lastCoords.y;
    return dx * dx + dy * dy;
  }
  onScroll(_event) {
    if (!this.isScrollDriven || this.tracker.trackedPointersCount === 0) {
      return;
    }
    this.scrollDetected = true;
    this.tryScrollDrivenActivation();
  }
  tryScrollDrivenActivation() {
    if (!this.scrollDetected || this.state !== State.BEGAN) {
      return;
    }

    // Require some pointer travel on top of the scroll event — momentum
    // scrolling keeps emitting `scroll` events after a touch that was only
    // meant to stop it, and that touch must not activate the handler.
    if (this.pointerTravelSq() >= this.scrollActivationThresholdSq) {
      this.activate();
    }
  }
  onPointerLeave() {
    if (this.state === State.BEGAN || this.state === State.ACTIVE) {
      this.cancel();
    }
  }
  onPointerUp(event) {
    super.onPointerUp(event);
    this.onUp(event);
  }
  onPointerRemove(event) {
    super.onPointerRemove(event);
    this.onUp(event);
  }
  onUp(event) {
    this.tracker.removeFromTracker(event.pointerId);
    if (this.tracker.trackedPointersCount === 0) {
      if (this.role === NativeGestureRole.Button && this.state === State.BEGAN) {
        this.activate();
      }
      if (this.state === State.ACTIVE) {
        this.end();
      } else {
        this.fail();
      }
    }
  }
  shouldRecognizeSimultaneously(handler) {
    if (super.shouldRecognizeSimultaneously(handler)) {
      return true;
    }
    if (handler instanceof NativeViewGestureHandler && handler.state === State.ACTIVE && handler.disallowsInterruption() && !handler.yieldsToContinuousGestures) {
      return false;
    }
    const canBeInterrupted = !this.disallowInterruption || this.yieldsToContinuousGestures && handler.isContinuous;
    if (this.state === State.ACTIVE && handler.state === State.ACTIVE && canBeInterrupted) {
      return false;
    }
    return this.state === State.ACTIVE && canBeInterrupted && handler.handlerTag > 0;
  }
  detach() {
    this.clearLongPressTimer();
    super.detach();
    this.role = null;
  }
  shouldBeCancelledByOther(handler) {
    return !this.disallowInterruption || this.yieldsToContinuousGestures && handler.isContinuous;
  }
  shouldAttachGestureToChildView() {
    return true;
  }
  disallowsInterruption() {
    return this.disallowInterruption;
  }
  isButton() {
    return this.role === NativeGestureRole.Button;
  }
  isManagedButton() {
    return this.isButton() && this.actionType === ActionType.NONE;
  }
  shouldBeginWithRecordedHandlers(recorded) {
    if (!this.isButton()) {
      return true;
    }
    const self = this;
    return recorded.every(other => other.shouldRecognizeSimultaneously(self) || self.shouldRecognizeSimultaneously(other) || other.delegate.view === this.delegate.view || other.name === SingleGestureName.Hover);
  }
  onCancel() {
    super.onCancel();
    dispatchGestureLifecycleEvent(this.delegate.view, GestureLifecycleEvent.Canceled);
  }
  onStateChange(newState) {
    if (!this.isManagedButton()) {
      return;
    }
    if (newState === State.BEGAN) {
      if (!this.getButtonEventData().pointerInside) {
        return;
      }
      this.dispatchButtonEvent(ButtonEventName.PressIn);
      this.longPressDetected = false;
      if (this.hasLongPressHandler && this.longPressDuration >= 0) {
        this.pendingLongPress = setTimeout(() => {
          this.pendingLongPress = null;
          this.longPressDetected = true;
          this.dispatchButtonEvent(ButtonEventName.LongPress);
        }, this.longPressDuration);
      }
      return;
    }
    if (newState !== State.END && newState !== State.FAILED && newState !== State.CANCELLED) {
      return;
    }
    const endedInside = this.lastEventWasInside;
    if (endedInside) {
      this.dispatchButtonEvent(ButtonEventName.PressOut);
    }
    this.clearLongPressTimer();
    if (newState === State.END && !this.longPressDetected && endedInside) {
      this.dispatchButtonEvent(ButtonEventName.Press);
    }
    this.dispatchButtonEvent(ButtonEventName.InteractionFinished);
    this.longPressDetected = false;
  }
  dispatchButtonEvent(name) {
    if (name === ButtonEventName.PressIn) {
      this.lastEventWasInside = true;
    } else if (name === ButtonEventName.PressOut) {
      this.lastEventWasInside = false;
    }
    dispatchButtonEvent(this.delegate.view, name, this.getButtonEventData());
  }
  getButtonEventData() {
    const absolute = this.tracker.getAbsoluteCoordsAverage();
    const relative = this.tracker.getRelativeCoordsAverage();
    return {
      pointerInside: this.delegate.isPointerInBounds(absolute),
      x: relative.x,
      y: relative.y,
      absoluteX: absolute.x,
      absoluteY: absolute.y,
      numberOfPointers: this.tracker.trackedPointersCount,
      pointerType: this.pointerType
    };
  }
  clearLongPressTimer() {
    if (this.pendingLongPress !== null) {
      clearTimeout(this.pendingLongPress);
      this.pendingLongPress = null;
    }
  }
  transformNativeEvent() {
    const absolute = this.tracker.getAbsoluteCoordsAverage();
    const relative = this.tracker.getRelativeCoordsAverage();
    return {
      pointerInside: this.delegate.isPointerInBounds(absolute),
      x: relative.x,
      y: relative.y,
      absoluteX: absolute.x,
      absoluteY: absolute.y
    };
  }
  arePointerStatesEqual(a, b) {
    return a.pointerInside === b.pointerInside && a.numberOfPointers === b.numberOfPointers && a.pointerType === b.pointerType;
  }
  shouldSuppressActiveUpdate(handlerData) {
    const last = this.lastActiveHandlerData;
    if (last && this.arePointerStatesEqual(last, handlerData)) {
      return true;
    }
    this.lastActiveHandlerData = handlerData;
    return false;
  }
  reset() {
    this.clearLongPressTimer();
    super.reset();
    this.lastActiveHandlerData = null;
    this.lastEventWasInside = false;
    this.longPressDetected = false;
    this.scrollDetected = false;
  }
  onDestroy() {
    this.clearLongPressTimer();
    super.onDestroy();
  }
}
//# sourceMappingURL=NativeViewGestureHandler.js.map